import { useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { ScreenPreview, EditableAdvancedLoopScreen, EditableTableScreen } from "./";
import { Notification } from "../client/Notification";
import { addNewLoopEntry } from "../../store/actions/client";
import { loopClientEntriesSelector, clientEntriesSelector } from "../../store/selectors/clientEntries";
import { entriesSelector } from "../../store/selectors/entities/entries";
import { sheetsSelector } from "../../store/selectors/entities/sheets";
import { addNewLoopEntrySelector, interactiveClientSelector, allowLoopEditsSelector, prePopulationLimitSelector } from "../../store/selectors/clientSettings";
import { conditionsSelector } from "../../store/selectors/entities/conditions";
import { conditionCheck, conditionalScreenFilter } from "../../lib/conditions";
import { MIN_TABLE_COLS, MIN_TABLE_ROWS } from "../../lib/sheets";
import { SINGLE_CHOICE_DUPLICATE_STYLES } from "../../lib/fields";
import { guid, getStaticLoopValue, createNewRow, isPrePopTableSheet, isLabelsAboveTableSheet, isTrueStringMatch } from "../../lib/utils";
import { applyValidations } from "../../lib/fields";
import { usePreFillRowsEffect } from "../../lib/customHooks/usePreFillEffect";
const filterDuplicatesOf = valueSet => (acc, {
  text
}) => {
  if (valueSet.has(text)) {
    acc.push(text);
  }
  return acc;
};
export const LoopTableViewPreview = ({
  keyField,
  entries,
  sheet
}) => {
  const allowDuplicates = sheet.allow_duplicate;
  const previewEntries = useSelector(clientEntriesSelector);
  const conditions = useSelector(conditionsSelector);
  const dispatch = useDispatch();
  const firstSubSheetId = sheet.subSheets?.[0] ?? null;
  const isUserAddingNewLoopEntry = useSelector(addNewLoopEntrySelector);
  const allEntries = [keyField, ...entries];
  const thisRowKeyFieldValues = new Set();
  const entryValuesLength = keyField?.entry_values?.length;

  // accounts for the subsheet of a simple loop screen being conditional
  const isSubSheetVisible = conditionalScreenFilter({
    id: firstSubSheetId
  }, conditions, previewEntries);

  // TODO: optimize for loops
  // this loops over the entries twice, but no choice unless we refactor
  // the full adjustedPreviewEntries is necessary in order to run conditionCheck
  // would be better to do this outside this component
  const adjustedPreviewEntries = previewEntries.map(entry => {
    const {
      rowGuid,
      parentGuid,
      value
    } = entry;
    if (keyField.guid === parentGuid && value) {
      thisRowKeyFieldValues.add(value);
    }
    if (rowGuid === isUserAddingNewLoopEntry) {
      return {
        ...entry,
        guid: parentGuid
      };
    }
    return entry;
  });
  const tablePreviewValues = previewEntries.reduce((acc, entry) => {
    const {
      rowGuid,
      parentGuid,
      entry_values,
      sheet_id,
      reference_data_column
    } = entry;
    if (rowGuid === isUserAddingNewLoopEntry && (isSubSheetVisible || sheet_id !== firstSubSheetId)) {
      let currentlySelectedOptions = [];

      // Only filter duplicates if not allowed
      if (!allowDuplicates) {
        if (reference_data_column) {
          // Use row key field values directly if we have a reference column
          currentlySelectedOptions = [...thisRowKeyFieldValues];
        } else {
          // Otherwise filter entry values to match the key field values
          currentlySelectedOptions = entry_values.reduce(filterDuplicatesOf(thisRowKeyFieldValues), []);
        }
      }
      acc.push({
        conditionMet: conditionCheck(parentGuid, adjustedPreviewEntries, conditions, rowGuid),
        ...applyValidations({
          removeDuplicates: !allowDuplicates,
          currentlySelectedOptions,
          ...entry
        })
      });
    }
    return acc;
  }, []);
  const SummaryScreenComponent = isLabelsAboveTableSheet(sheet) ? LabelsAbovePreview : TableViewPreview;
  const canAddNewRow = allowDuplicates || !entryValuesLength || entryValuesLength > thisRowKeyFieldValues.size;
  return <>
      {isUserAddingNewLoopEntry ? <ScreenPreview entries={tablePreviewValues} /> : <SummaryScreenComponent entries={allEntries} canAddNewRow={canAddNewRow} addNewLoopEntry={() => {
      const newLoopRow = createNewRow(allEntries);
      dispatch(addNewLoopEntry(newLoopRow));
    }} sheet={sheet} />}
    </>;
};
const LabelsAbovePreview = ({
  entries,
  addNewLoopEntry,
  sheet
}) => {
  const loopValues = useSelector(loopClientEntriesSelector);
  const interactiveClient = useSelector(interactiveClientSelector) || false;
  const [keyField] = entries;
  const {
    display_entry
  } = sheet;
  const {
    label: displayFieldLabel,
    guid: displayFieldGuid
  } = useMemo(() => {
    return display_entry?.guid ? entries.find(({
      guid
    }) => guid === display_entry.guid) : keyField;
  }, [display_entry, entries, keyField]);

  // only need the display field from each loop/row
  const loopRows = loopValues.reduce((rows, entry) => {
    if (entry.parentGuid == displayFieldGuid) {
      rows.push(entry);
    }
    return rows;
  }, []);
  return <div className="labels-above--preview">
      <div className="labels-above-view--wrapper">
        <div className="labels-above--header">{displayFieldLabel}</div>
        <div className="labels-above--values">
          {loopRows.map(({
          value,
          guid
        }, i) => <div className="labels-above--value" key={`row-${guid}`}>
              {value}
            </div>)}
        </div>
      </div>
      <div className="labels-above--controls">
        {!interactiveClient && <Notification withWrapper={true}>
            Users can add as many rows as they want.
          </Notification>}
        <button className="foundry--button button--active btn--sm add-item button--contained" onClick={addNewLoopEntry}>
          <FontAwesomeIcon className="preview--loop--addItem" icon={faPlus} />
          <span className="button--label">Add Item</span>
        </button>
      </div>
    </div>;
};
const TableViewPreview = ({
  entries,
  addNewLoopEntry,
  sheet,
  canAddNewRow
}) => {
  const loopValues = useSelector(loopClientEntriesSelector);
  const interactiveClient = useSelector(interactiveClientSelector) || false;
  const prePopulationLimit = useSelector(prePopulationLimitSelector);
  const activeCol = useRef(null);
  const isPrePopTable = isPrePopTableSheet(sheet);
  const allowDuplicates = Boolean(sheet.allow_duplicate);
  const {
    style,
    type
  } = entries[0]; // keyFeild will always exist
  const showDropdownDuplicates = type == 'Dropdown' && SINGLE_CHOICE_DUPLICATE_STYLES.includes(style);
  const shouldPrefillDuplicates = allowDuplicates && showDropdownDuplicates;
  const displayFieldGuid = sheet.display_entry ? sheet.display_entry.guid : null;
  const shouldPrefill = interactiveClient && isPrePopTable;

  // pad collumns
  let allEntries = entries.length > MIN_TABLE_COLS ? entries : [...entries, ...Array(MIN_TABLE_COLS - entries.length).fill({
    empty: 'empty',
    label: '',
    default: '',
    guid: guid()
  })];
  if (displayFieldGuid) {
    // This mimics the behavior on mobile when a display field is used in a table
    // The key field is hidden and the display field takes it's place in the first column
    const displayFieldEntry = allEntries.find(entry => entry.guid === displayFieldGuid);
    allEntries = allEntries.filter(entry => !entry.keyField);
    allEntries.unshift(displayFieldEntry);
  }

  // Grab all the rowGuids by sheet
  const rowGuids = Array.from(loopValues.reduce((acc, {
    sheet_id,
    rowGuid
  }) => {
    if (isTrueStringMatch(sheet_id, sheet.id)) {
      acc.add(rowGuid);
    }
    return acc;
  }, new Set()));

  // Add any additional rows to pad out the table
  const prepareRows = rows => {
    const rowsMinimum = MIN_TABLE_ROWS;
    if (rows.length < rowsMinimum) {
      const rowsNeeded = Array(rowsMinimum - rows.length).fill({
        value: ''
      });
      return rows.concat(rowsNeeded);
    }
    return rows;
  };
  const smartPreviewItemLoopLookup = guid => {
    const rows = prepareRows(rowGuids);
    return rows.map((rowGuid, i) => {
      // Find all values for a given row.
      const {
        type,
        value
      } = loopValues.find(loopItem => {
        if (loopItem.parentGuid == guid && loopItem.rowGuid === rowGuid) {
          return true;
        }
      }) || {
        value: '',
        type: ''
      };
      const columnValue = getStaticLoopValue(type, value);
      return <div className="cell" key={`col-${i}`}>
          <div className="loop--value userAdded">{columnValue}</div>
        </div>;
    });
  };
  usePreFillRowsEffect({
    interactiveClient,
    shouldPrefill,
    sheet,
    entries,
    limit: prePopulationLimit,
    shouldPrefillDuplicates
  }, [shouldPrefill, interactiveClient, sheet]);
  return <div className="tableview--preview">
      <div className="table-view--wrapper">
        <div className="table">
          {allEntries.map(({
          guid,
          label,
          default: defaultValue,
          empty = ''
        }, i) => {
          const active = false;
          const style = {};
          const first = i === 0;
          return <div className={`col ${empty} ${active ? 'col--active' : ''}`} key={`${label}-key-${i}`} ref={active || first ? activeCol : null} style={style}>
                  <div className="field--name">{label}</div>
                  <div className="cell default--values">
                    <div className="loop--value">{defaultValue}</div>
                  </div>
                  {smartPreviewItemLoopLookup(guid)}
                </div>;
        })}
        </div>
      </div>
      {!interactiveClient && <Notification>Users can add as many rows as they want.</Notification>}
      {interactiveClient && isPrePopTable && <Notification withWrapper={true}>
          This is a preview. Not all rows are shown or auto-filled.
        </Notification>}
      {canAddNewRow && <button className="foundry--button button--active btn--sm add-item button--contained" onClick={addNewLoopEntry}>
          <FontAwesomeIcon className="preview--loop--addItem" icon={faPlus} />
          <span className="button--label">Add Item</span>
        </button>}
    </div>;
};
export const AdvancedLoopScreen = ({
  keyField,
  sheet
}) => {
  const allowDuplicates = !!sheet.allow_duplicate;
  const previewEntries = useSelector(clientEntriesSelector);
  const loopValues = useSelector(loopClientEntriesSelector);
  const interactiveClient = useSelector(interactiveClientSelector) || false;
  const conditions = useSelector(conditionsSelector);
  const dispatch = useDispatch();
  const isUserAddingNewLoopEntry = useSelector(addNewLoopEntrySelector);
  const rows = loopValues.filter(({
    sheet_id
  }) => sheet_id === sheet.id);
  // For advanced loops, we only need the key field until
  // we add support for the nested fields in the preview
  const allEntries = [keyField];
  const thisRowKeyFieldValues = new Set();
  const entryValuesLength = keyField?.entry_values?.length;
  const adjustedPreviewEntries = previewEntries.map(entry => {
    const {
      rowGuid,
      parentGuid,
      value
    } = entry;
    if (keyField.guid === parentGuid && value) {
      thisRowKeyFieldValues.add(value);
    }
    if (rowGuid === isUserAddingNewLoopEntry) {
      return {
        ...entry,
        guid: parentGuid
      };
    }
    return entry;
  });
  const tablePreviewValues = previewEntries.reduce((acc, entry) => {
    const {
      rowGuid,
      parentGuid,
      entry_values,
      reference_data_column
    } = entry;
    if (rowGuid === isUserAddingNewLoopEntry) {
      let currentlySelectedOptions = [];

      // Only filter duplicates if not allowed
      if (!allowDuplicates) {
        if (reference_data_column) {
          // Use row key field values directly if we have a reference column
          currentlySelectedOptions = [...thisRowKeyFieldValues];
        } else {
          // Otherwise filter entry values to match the key field values
          currentlySelectedOptions = entry_values.reduce(filterDuplicatesOf(thisRowKeyFieldValues), []);
        }
      }
      acc.push({
        conditionMet: conditionCheck(parentGuid, adjustedPreviewEntries, conditions, rowGuid),
        ...applyValidations({
          removeDuplicates: !allowDuplicates,
          currentlySelectedOptions,
          ...entry
        })
      });
    }
    return acc;
  }, []);
  const addNewLoopItem = () => {
    const newLoopRow = createNewRow(allEntries);
    dispatch(addNewLoopEntry(newLoopRow));
  };
  const canAddNewRow = allowDuplicates || !entryValuesLength || entryValuesLength > thisRowKeyFieldValues.size;
  return <>
      {isUserAddingNewLoopEntry ? <ScreenPreview entries={tablePreviewValues} /> : <div className="advanced-loop--preview">
          <div className="row--values">
            <div className="key-field preview-field">{keyField.label}</div>
            {rows.map((row, i) => {
          return <div className="row-value--preview preview-field" key={i}>
                  {row.value}
                </div>;
        })}
          </div>
          {canAddNewRow && <button className="foundry--button button--active button--contained btn--sm" onClick={addNewLoopItem}>
              <FontAwesomeIcon className="preview--loop--addItem" icon={faPlus} />
              <span className="button--label">Add Item</span>
            </button>}
        </div>}
      {interactiveClient && <div className="advanced-looop--notification notification">
          <div className="notification--icon">
            <i className="foundry-icon icon--info icon--gray" />
          </div>
          <div className="notification--msg">
            Repeating screens are not fully supported in preview mode.
          </div>
        </div>}
    </>;
};
export const LoopScreenPreview = ({
  sheet,
  ignoreConditions
}) => {
  const allowLoopEdits = useSelector(allowLoopEditsSelector);
  const sheets = useSelector(sheetsSelector);
  const allEntries = useSelector(entriesSelector);
  const subSheet = sheets.find(({
    section_id
  }) => section_id === sheet.multi_section);
  const screenDescription = sheet.description;
  const keyField = allEntries.find(({
    sheet_id
  }) => sheet.id === sheet_id);

  // List screens do not have subsheets, but are loops
  const subSheetEntries = subSheet ? allEntries.filter(({
    sheet_id
  }) => `${subSheet.id}` === `${sheet_id}`) : [];

  // TODO: Reconcile these components so that we can
  // use the same component for both, if possible
  const dispatchLoopScreenComponents = {
    13: EditableAdvancedLoopScreen,
    15: EditableTableScreen
  };
  const clientLoopScreenComponents = {
    13: AdvancedLoopScreen,
    15: LoopTableViewPreview
  };
  const tableComponents = allowLoopEdits ? dispatchLoopScreenComponents : clientLoopScreenComponents;
  const TableComponent = tableComponents[`${sheet.sheet_type_id}`];
  return <TableComponent ignoreConditions={ignoreConditions} screenName={screenDescription} keyField={keyField} entries={subSheetEntries} sheet={sheet} allowDuplicates={!!sheet.allow_duplicate} />;
};